import { distance } from "fastest-levenshtein";

import { AgentsZapierFacade } from ".";
import { getImplementation } from "./implementations";
import { components } from "./monolith-v4-api";
import {
  AuthenticationSchema,
  TestAuthResponse,
  ZapierFacadeError,
} from "./types";

export function implementationNeedsAuth(
  implementation: components["schemas"]["Implementation"],
) {
  return (
    implementation.auth_type === "oauth" || implementation.auth_type === "other"
  );
}

/** Get the text to use for matching an authentication. */
function getAuthText(auth: components["schemas"]["Authentication"]): string {
  if (auth.title) {
    return auth.title;
  }

  if (auth.label) {
    return auth.label;
  }

  return auth.id?.toString() ?? "";
}

/**
 * Using the user's email and name, tries to select the best authentication to use for them
 * based on the given list of authentications.
 */
function selectBestAuthentication({
  authentications,
  userEmail,
  userFullName,
  userCustomId,
}: {
  authentications: components["schemas"]["Authentication"][];
  userEmail: string;
  userFullName: string;
  userCustomId: number | string;
}): components["schemas"]["Authentication"] | undefined {
  // Filter out stale authentications
  const notStaleAuths = authentications.filter((auth) => !auth.is_stale);

  if (notStaleAuths.length === 0) {
    return undefined;
  }

  // Calculate similarity scores against email and full name
  const scores = notStaleAuths.map((auth) => {
    const text = getAuthText(auth);
    const emailScore = distance(userEmail, text);
    const nameScore = distance(userFullName, text);

    return {
      auth,
      score: Math.max(emailScore, nameScore),
    };
  });

  // Find first auth with score >= 0.7
  const bestMatch = scores.find(({ score }) => score >= 0.7);
  if (bestMatch) {
    return bestMatch.auth;
  }

  // Fall back to first auth owned by user
  const userOwned = notStaleAuths.find(
    (auth) =>
      auth.customuser_id ===
      (typeof userCustomId === "string"
        ? parseInt(userCustomId)
        : userCustomId),
  );

  return userOwned;
}

export async function getAuthenticationsByIds(
  facade: AgentsZapierFacade,
  params: {
    ids: number[];
    accountId: string;
  },
): Promise<{ results: AuthenticationSchema[] }> {
  const { ids, accountId } = params;

  if (ids.length === 0) {
    return { results: [] };
  }

  const { data, error, response } = await facade.api.GET(
    "/api/v4/authentications/",
    {
      params: {
        query: {
          ids: ids.join(","),
          account_id: accountId,
          limit: ids.length,
        },
      },
    },
  );

  if (error) {
    throw new ZapierFacadeError(
      "/api/v4/authentications/",
      "GET",
      params,
      response,
      error,
    );
  }

  // TODO generated types from the monolith are wrong here!
  const { results } = data as unknown as {
    results: components["schemas"]["Authentication"][];
  };

  // TODO once we're fully migrated to monolith, we can remove this cast
  return { results: results as unknown as AuthenticationSchema[] };
}

export async function getAuthentications(
  facade: AgentsZapierFacade,
  params: {
    app: string;
    accountId: string;
    userEmail: string;
    userFullName: string;
    userCustomId: number | string;
  },
): Promise<{
  results: AuthenticationSchema[];
  message?: string;
}> {
  const { app, accountId } = params;

  const implementation = await getImplementation(facade, {
    selectedApi: app,
  });

  const needsAuth = implementationNeedsAuth(implementation);

  if (!needsAuth) {
    console.warn(`${app} does not need auth.`);
    return {
      results: [],
      message: `${app} does not need auth.`,
    };
  }

  const { data, error, response } = await facade.api.GET(
    "/api/v4/authentications/",
    {
      params: {
        query: {
          selected_api: implementation.selected_api,
          account_id: accountId,
          limit: 2000,
          exclude_unusable: true,
          ordering: "-lastchanged,personal_first",
          include_compatible: "true",
        },
      },
    },
  );

  if (error) {
    throw new ZapierFacadeError(
      "/api/v4/authentications/",
      "GET",
      params,
      response,
      error,
    );
  }

  // TODO generated types from the monolith are wrong here!
  const authentications = (
    data as unknown as {
      results: components["schemas"]["Authentication"][];
    }
  ).results;

  const bestAuthentication = selectBestAuthentication({
    authentications,
    userEmail: params.userEmail,
    userFullName: params.userFullName,
    userCustomId: params.userCustomId,
  });

  if (bestAuthentication) {
    return {
      results: [
        bestAuthentication,
        ...authentications.filter((auth) => auth.id !== bestAuthentication.id),
        // TODO once we're fully migrated to monolith, we can remove this cast
      ] as unknown as AuthenticationSchema[],
    };
  }

  return {
    // TODO once we're fully migrated to monolith, we can remove this cast
    results: authentications as unknown as AuthenticationSchema[],
  };
}

export const testAuth = async (
  facade: AgentsZapierFacade,
  params: {
    authId: number;
  },
): Promise<TestAuthResponse> => {
  const { data, error, response } = await facade.api.POST(
    "/api/v4/authentications/{id}/test/",
    {
      params: {
        path: {
          id: params.authId,
        },
      },

      // @ts-expect-error -- body is not actually needed
      body: {},
    },
  );

  if (response.status === 400) {
    return {
      // @ts-expect-error -- incorrect monolith types
      success: data?.success ?? false,
      // @ts-expect-error -- incorrect monolith types
      errors: data?.errors ?? null,
      // @ts-expect-error -- incorrect monolith types
      results: data?.results ?? null,
    };
  }

  if (error) {
    throw new ZapierFacadeError(
      "/api/v4/authentications/{id}/test/",
      "POST",
      params,
      response,
      error,
    );
  }

  return {
    success: true,
    // @ts-expect-error -- incorrect monolith types
    results: data?.results,
    errors: null,
  };
};
